"use client"

import { Button, ButtonProps } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { GoogleLogo, Microsoft } from "./google"
import {
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  AuthProvider,
  getRedirectResult,
  UserCredential,
  AuthErrorCodes,
  ProviderId,
} from "firebase/auth"
import { useLogin } from "@/api"
import { FeatureFlag, useFeatureFlag } from "@/hooks"
import React, { FC, useEffect, useState } from "react"
import Link from "next/link"
import { GitHubLogoIcon } from "@radix-ui/react-icons"
import { toast } from "sonner"
import { LoadingSpinner } from "./loading.spinner"
import { auth } from "@/lib/firebase"
import { useMutation, useQuery } from "@tanstack/react-query"
import { FirebaseError } from "firebase/app"
import { EmailForm } from "./email-auth-form"
// PROVIDERS
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope("https://www.googleapis.com/auth/userinfo.email")
googleProvider.addScope("https://www.googleapis.com/auth/userinfo.profile")
googleProvider.setCustomParameters({
  // Force re-consent.
  prompt: "consent",
})

const microsoftProvider = new OAuthProvider("microsoft.com")

const githubProvider = new GithubAuthProvider()
githubProvider.addScope("user:email")

type ProviderId = (typeof ProviderId)[keyof typeof ProviderId] | "microsoft.com"

type UseAuthProps = {
  onError: (msg: string) => void
  onWaitlist: () => void
}

const useAuth = ({ onError, onWaitlist }: UseAuthProps) => {
  const onLoginError = (error: Error) => {
    if (error.message.includes("Signups are currently closed")) {
      onWaitlist()
    } else {
      onError(formatError(error))
    }
  }

  const login = useLogin({ onError: onLoginError })

  const processCredential = async (credential: UserCredential) => {
    const { user, providerId } = credential
    if (!providerId) {
      throw new Error("Provider ID not found")
    }
    const oauthAccessToken = getOAuthAccessToken(credential, providerId as ProviderId)?.accessToken
    const idToken = await user.getIdToken()
    const refreshToken = user.refreshToken
    login.mutate({ idToken, refreshToken, providerId, oauthAccessToken })
  }

  const handleError = (err: unknown) => {
    if (err instanceof FirebaseError) {
      return onError(handleAuthError(err))
    }
    return onError(formatError(err))
  }

  const redirectQuery = useQuery({
    queryKey: ["authRedirectResult"],
    queryFn: async () => {
      const credential = await getRedirectResult(auth)
      if (!credential) {
        return null
      }
      await processCredential(credential)
      return null
    },
    enabled: process.env.NODE_ENV !== "development",
    retry: false,
  })

  useEffect(() => {
    if (redirectQuery.isError) {
      handleError(redirectQuery.error)
    }
  }, [redirectQuery.isError])

  const popupMutation = useMutation({
    mutationFn: async (provider: AuthProvider) => {
      const credential = await signInWithPopup(auth, provider)
      return await processCredential(credential)
    },
    onError: handleError,
    retry: false,
  })

  const popup = (provider: AuthProvider) => popupMutation.mutate(provider)

  const redirect = (provider: AuthProvider) => signInWithRedirect(auth, provider)

  const signInWithProvider = async (provider: AuthProvider) =>
    process.env.NODE_ENV === "development" ? popup(provider) : redirect(provider)

  const loading = redirectQuery.isFetching || popupMutation.isPending || login.isPending

  return { loading, signInWithProvider }
}

export function UserAuthForm({ isEmail }: { isEmail?: boolean }) {
  const [waitlistOpen, setWaitlistOpen] = useState(false)
  const loginWithAzure = useFeatureFlag(FeatureFlag.loginWithAzure)

  const authProps = {
    onError: toast.error,
    onWaitlist: () => setWaitlistOpen(true),
  }

  const { loading, signInWithProvider } = useAuth(authProps)

  const providerButtonProps = (provider: AuthProvider): ButtonProps => ({
    variant: "outline",
    type: "button",
    onClick: () => signInWithProvider(provider),
  })

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner size={40} className="w-full" />
      </div>
    )
  }

  return (
    <Dialog open={waitlistOpen} onOpenChange={(open) => setWaitlistOpen(open)}>
      {isEmail ? (
        <div className="grid gap-6">
          <EmailForm />
        </div>
      ) : (
        <>
          <div className="grid gap-6">
            <Button {...providerButtonProps(googleProvider)}>
              <GoogleLogo className="mr-2 size-4" />
              Google
            </Button>
            <Button {...providerButtonProps(githubProvider)}>
              <GitHubLogoIcon className="mr-2 size-4" />
              GitHub
            </Button>
            {loginWithAzure && (
              <Button {...providerButtonProps(microsoftProvider)}>
                <Microsoft className="mr-2 size-4" />
                Microsoft
              </Button>
            )}
          </div>
          <WaitlistDialogContent />
        </>
      )}
    </Dialog>
  )
}

const WaitlistDialogContent: FC = () => {
  return (
    <DialogContent>
      <DialogTitle>Join the waitlist</DialogTitle>
      <DialogDescription>
        Signups are currently closed. Existing users can still log in and use the platform.
        <br /> <br />
        If you want to be notified of when signups are open, please apply for the waitlist{" "}
        <Link
          rel="noopener noreferrer"
          target="_blank"
          href="https://cosine.sh/register"
          className="text-primary underline underline-offset-4 hover:text-muted-foreground"
        >
          here
        </Link>
      </DialogDescription>
    </DialogContent>
  )
}

const getOAuthAccessToken = (credential: UserCredential, providerId: ProviderId) => {
  switch (providerId) {
    case "github.com":
      return GithubAuthProvider.credentialFromResult(credential)
    case "google.com":
      return GoogleAuthProvider.credentialFromResult(credential)
    case "microsoft.com":
      return OAuthProvider.credentialFromResult(credential)
    default:
      return null
  }
}

const providers: Partial<Record<ProviderId, string>> = {
  "google.com": "Google",
  "github.com": "GitHub",
  "microsoft.com": "Microsoft",
}

const handleAuthError = (err: FirebaseError) => {
  switch (err.code) {
    case AuthErrorCodes.NEED_CONFIRMATION:
      const { customData } = err
      const email = customData?.email as string | undefined
      if (!email) {
        return "An account with this email already exists using a different sign-in method."
      }
      const data = customData?._tokenResponse as Record<string, any> | undefined
      const provider = data?.verifiedProvider?.find((s: string) => Boolean(s)) as
        | ProviderId
        | undefined
      if (!provider) {
        return `An account with this email ${email} already exists using a different sign-in method.`
      }
      const method = providers[provider] ?? provider
      return `An account with this email ${email} already exists using the ${method} sign-in method.`
    default:
      return err.message
  }
}

const formatError = (error: unknown) => {
  const userMessage = "There was an issue logging in"
  const devMessage = error instanceof Error ? error.message : "An unknown error occurred"
  return process.env.NODE_ENV === "development" ? devMessage : userMessage
}
